import consumer from "../../shared/utils/cable_consumer"

export function ReturnExchangeChat() {
  return {
    isLoading: false,
    isAuthenticated: false,
    currentConversationId: null,
    brandId: null,
    returnExchangeId: null,
    brandName: '',
    messages: [],
    messageInput: '',
    isConnected: false,
    isComposing: false,
    chatSubscription: null,
    onlineUsers: new Set(),
    connectionRetryCount: 0,
    maxRetryAttempts: 3,
    retryDelay: 2000,
    
    init() {
      this.brandId = this.$el.dataset.brandId;
      this.returnExchangeId = this.$el.dataset.returnExchangeId;
      this.brandName = this.$el.dataset.brandName;
      
      this.checkAuthentication().then(authenticated => {
        this.isAuthenticated = authenticated;
        if (authenticated) {
          this.loadConversation();
        }
      });
    },

    async checkAuthentication() {
      try {
        console.log('[ReturnExchangeChat] 檢查使用者認證狀態...');
        const response = await fetch('/chat/auth_status', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('[ReturnExchangeChat] 認證回應:', data);
          return !!data.authenticated;
        }
        
        console.warn('[ReturnExchangeChat] 認證檢查失敗:', response.status, response.statusText);
        return false;
      } catch (error) {
        console.error('[ReturnExchangeChat] 認證檢查錯誤:', error);
        const csrfToken = document.querySelector('meta[name="csrf-token"]');
        return !!csrfToken;
      }
    },

    async loadConversation() {
      this.isLoading = true;
      console.log('[ReturnExchangeChat] 載入對話 - 品牌ID:', this.brandId, '退換貨ID:', this.returnExchangeId);

      try {
        const url = `/chat/messages?brand_id=${this.brandId}&return_exchange_id=${this.returnExchangeId}`;
        console.log('[ReturnExchangeChat] 請求 URL:', url);
        
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
          }
        });

        console.log('[ReturnExchangeChat] 回應狀態:', response.status, response.statusText);

        if (response.status === 401) {
          console.warn('[ReturnExchangeChat] 未授權訪問');
          this.isAuthenticated = false;
          return;
        }

        if (response.status === 422) {
          const errorData = await response.json();
          console.error('[ReturnExchangeChat] 無法載入對話:', errorData);
          
          // 如果是對話不存在，嘗試重新載入
          if (this.connectionRetryCount < this.maxRetryAttempts) {
            this.connectionRetryCount++;
            console.log(`[ReturnExchangeChat] 重試載入對話 (${this.connectionRetryCount}/${this.maxRetryAttempts})`);
            setTimeout(() => {
              this.loadConversation();
            }, this.retryDelay);
            return;
          }
          
          console.error('[ReturnExchangeChat] 超過最大重試次數，停止嘗試');
          this.isAuthenticated = false;
          return;
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[ReturnExchangeChat] 載入失敗:', response.status, response.statusText, errorText);
          throw new Error(`Failed to load messages: ${response.status} ${response.statusText}\n${errorText}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.error('[ReturnExchangeChat] 回應格式錯誤，Content-Type:', contentType);
          this.isAuthenticated = false;
          return;
        }

        const data = await response.json();
        console.log('[ReturnExchangeChat] 成功載入對話資料:', data);
        
        this.messages = data.messages || [];
        this.currentConversationId = data.conversation_id;
        
        console.log(`[ReturnExchangeChat] 載入了 ${this.messages.length} 則訊息，對話ID: ${this.currentConversationId}`);
        
        // 重置重試計數
        this.connectionRetryCount = 0;
        
        this.connectToChat();
        
        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
      } catch (error) {
        console.error('[ReturnExchangeChat] 載入對話失敗:', error);
        
        // 如果載入失敗，嘗試重新載入
        if (this.connectionRetryCount < this.maxRetryAttempts) {
          this.connectionRetryCount++;
          console.log(`[ReturnExchangeChat] 因錯誤重試載入對話 (${this.connectionRetryCount}/${this.maxRetryAttempts})`);
          setTimeout(() => {
            this.loadConversation();
          }, this.retryDelay);
          return;
        }
        
        console.error('[ReturnExchangeChat] 超過最大重試次數，停止嘗試');
        this.isAuthenticated = false;
      } finally {
        this.isLoading = false;
        console.log('[ReturnExchangeChat] 載入對話完成，isAuthenticated:', this.isAuthenticated);
      }
    },

    connectToChat() {
      if (!this.currentConversationId || this.chatSubscription) {
        console.log('[ReturnExchangeChat] 跳過連接 - 對話ID:', this.currentConversationId, '現有訂閱:', !!this.chatSubscription);
        return;
      }

      console.log('[ReturnExchangeChat] 建立 WebSocket 連接，對話ID:', this.currentConversationId);
      this.chatSubscription = consumer.subscriptions.create(
        {
          channel: "ChatChannel",
          conversation_id: this.currentConversationId
        },
        {
          connected: () => {
            console.log('[ReturnExchangeChat] WebSocket 連接成功');
            this.isConnected = true;
            this.connectionRetryCount = 0;
            this.markMessagesAsRead();
          },

          disconnected: () => {
            console.log('[ReturnExchangeChat] WebSocket 連接斷開');
            this.isConnected = false;
            
            // 自動重連
            if (this.connectionRetryCount < this.maxRetryAttempts) {
              this.connectionRetryCount++;
              console.log(`[ReturnExchangeChat] 自動重連 WebSocket (${this.connectionRetryCount}/${this.maxRetryAttempts})`);
              setTimeout(() => {
                this.reconnectToChat();
              }, this.retryDelay);
            } else {
              console.warn('[ReturnExchangeChat] WebSocket 重連次數已達上限');
            }
          },

          received: (data) => {
            console.log('[ReturnExchangeChat] 收到 WebSocket 訊息:', data);
            this.handleWebSocketMessage(data);
          },

          rejected: () => {
            console.error('[ReturnExchangeChat] WebSocket 連接被拒絕');
            this.isConnected = false;
          }
        }
      );
    },

    disconnectFromChat() {
      if (this.chatSubscription) {
        this.chatSubscription.unsubscribe();
        this.chatSubscription = null;
        this.isConnected = false;
      }
    },

    handleWebSocketMessage(data) {
      switch (data.type) {
        case 'new_message':
          this.handleNewMessage(data.message);
          break;
        case 'messages_read':
          this.handleMessagesRead(data);
          break;
        case 'presence_update':
          this.handlePresenceUpdate(data);
          break;
        case 'error':
          console.error('WebSocket 錯誤:', data.errors);
          break;
      }
    },

    handleNewMessage(messageData) {
      const currentUserId = this.getCurrentUserId();
      messageData.is_own = messageData.user_id === currentUserId;
      
      const existingMessage = this.messages.find(msg => msg.id === messageData.id);
      if (existingMessage) {
        return;
      }
      
      if (messageData.is_own) {
        // 替換臨時訊息
        for (let i = this.messages.length - 1; i >= 0; i--) {
          const msg = this.messages[i];
          if (msg.temp === true && msg.content === messageData.content && msg.user_id === currentUserId) {
            this.messages[i] = messageData;
            setTimeout(() => {
              this.scrollToBottom();
            }, 10);
            return;
          }
        }
        return;
      }
      
      // 新增新訊息（只有別人的訊息）
      this.messages.push(messageData);
      setTimeout(() => {
        this.scrollToBottom();
      }, 10);
      
      // 如果不是自己的訊息，標記為已讀
      this.markMessagesAsRead();
    },

    handleMessagesRead(data) {
      // 更新訊息的已讀狀態
      data.message_ids.forEach(messageId => {
        const message = this.messages.find(msg => msg.id === messageId);
        if (message) {
          message.read = true;
        }
      });
    },

    handlePresenceUpdate(data) {
      if (data.status === 'online') {
        this.onlineUsers.add(data.user_id);
      } else {
        this.onlineUsers.delete(data.user_id);
      }
    },

    sendMessage() {
      const content = this.messageInput.trim();
      if (!content || !this.currentConversationId || !this.isConnected) {
        return;
      }

      // 立即顯示訊息
      const tempMessage = {
        id: 'temp_' + Date.now(),
        content: content,
        user_id: this.getCurrentUserId(),
        is_own: true,
        created_at: new Date().toISOString(),
        read: false,
        temp: true
      };

      this.messages.push(tempMessage);
      this.messageInput = '';
      
      setTimeout(() => {
        this.scrollToBottom();
      }, 10);

      // 透過 WebSocket 發送訊息
      this.chatSubscription.perform('send_message', {
        content: content,
        message_type: 'text'
      });
    },

    markMessagesAsRead() {
      if (this.chatSubscription && this.isConnected) {
        this.chatSubscription.perform('mark_as_read', {});
      }
    },

    handleKeydown(event) {
      if (event.key === "Enter" && !event.shiftKey && !this.isComposing) {
        event.preventDefault()
        this.sendMessage()
      }
    },

    handleCompositionStart() {
      this.isComposing = true
    },

    handleCompositionEnd() {
      this.isComposing = false
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('zh-TW', { 
        hour: '2-digit', 
        minute: '2-digit',
        month: 'short',
        day: 'numeric'
      });
    },

    getCSRFToken() {
      const token = document.querySelector('meta[name="csrf-token"]');
      return token ? token.getAttribute('content') : '';
    },

    getCurrentUserId() {
      // 首先嘗試從 meta tag 獲取
      const userMeta = document.querySelector('meta[name="current-user-id"]');
      if (userMeta) {
        const userId = parseInt(userMeta.getAttribute('content'));
        return userId;
      }
      
      return null;
    },

    // 重新連接到聊天
    reconnectToChat() {
      if (this.chatSubscription) {
        this.chatSubscription.unsubscribe();
        this.chatSubscription = null;
      }
      
      this.connectToChat();
    },

    // 手動重新連接
    manualReconnect() {
      this.connectionRetryCount = 0;
      this.disconnectFromChat();
      this.loadConversation();
    },

    // 輔助方法：檢查使用者是否在線
    isUserOnline(userId) {
      return this.onlineUsers.has(userId);
    },

    // 獲取連接狀態文字
    getConnectionStatus() {
      if (this.isLoading) return '載入中...';
      if (this.isConnected) return '已連接';
      if (this.connectionRetryCount > 0) return `連接中... (${this.connectionRetryCount}/${this.maxRetryAttempts})`;
      return '未連接';
    },

    // 檢查是否需要顯示重新連接按鈕
    shouldShowReconnectButton() {
      return !this.isLoading && !this.isConnected && this.connectionRetryCount >= this.maxRetryAttempts;
    }
  };
}